// Which groups are closed on a list, remembered per person and page across visits (Yehuda, Oct 26).
// Kept as "all closed or all open" plus the groups toggled the other way, so Collapse all also covers
// a carrier or person that shows up after tonight's sync.
import { useCallback, useState } from 'react';
import { personKey, readJson, writeJson } from '../shell/browser-store';

export type Collapsed = { all: boolean; flipped: string[] };

const open: Collapsed = { all: false, flipped: [] };

const isCollapsed = (v: unknown): v is Collapsed =>
  typeof v === 'object' &&
  v !== null &&
  typeof (v as Collapsed).all === 'boolean' &&
  Array.isArray((v as Collapsed).flipped) &&
  (v as Collapsed).flipped.every((k) => typeof k === 'string');

export const isClosed = (c: Collapsed, key: string) => c.all !== c.flipped.includes(key);

export function toggled(c: Collapsed, key: string): Collapsed {
  return c.flipped.includes(key)
    ? { all: c.all, flipped: c.flipped.filter((k) => k !== key) }
    : { all: c.all, flipped: [...c.flipped, key] };
}

export function useCollapsed(userId: string, page: string) {
  const key = personKey('collapsed', userId, page);
  // Kept with its key: going from My renewals to All renewals reads that page's choice.
  const [held, setHeld] = useState(() => ({
    key,
    value: readJson('local', key, open, isCollapsed),
  }));
  const state = held.key === key ? held.value : readJson('local', key, open, isCollapsed);
  const save = useCallback(
    (next: Collapsed) => {
      setHeld({ key, value: next });
      writeJson('local', key, next);
    },
    [key],
  );
  return {
    closed: (group: string) => isClosed(state, group),
    toggle: (group: string) => save(toggled(state, group)),
    setAll: (all: boolean) => save({ all, flipped: [] }),
  };
}
